/**
 * PATH: packages/email-hub/src/feature/compose/ComposeDock.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPOSE-COMPOSEDOCK-001
 * PURPOSE: The floating compose surface — ONE wider/larger dock with a
 *          tab per open compose window (Gmail-style multi-compose). The
 *          active tab's ComposePage is shown; every other stays mounted
 *          but hidden so switching tabs never loses in-progress content.
 * WHY:     Operator ask (2026-06-15): the popup "should be wider and
 *          larger … structured as a multi-tab layout". Replaces the old
 *          N-overlapping-mini-windows model (which stacked and overlapped
 *          off-screen) with a single draggable tabbed dock.
 * RELATED:
 *   - DockTabStrip.tsx: tab bar + new-tab + dock controls + drag handle.
 *   - DockComposeBody.tsx: one mounted ComposePage per window.
 *   - composeDockStore.ts: window model + activeId tab selection.
 *   - apps/<tenant>/src/features/email/compose/ComposeDock.tsx: re-export shim.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { emailPath } from '../lib/basePath'
import { tlsKey } from '../../_internal/utils/tenantStorage'
import {
  useComposeDockStore,
  seedFromIntent,
  titleForIntent,
  DOCK_MOBILE_BREAKPOINT,
} from './composeDockStore'
import DockTabStrip, { type DockTab } from './DockTabStrip'
import DockComposeBody from './DockComposeBody'
import {
  type DockSize,
  clampDockSize,
  resizeFromDelta,
  loadDockSize,
  saveDockSize,
  DOCK_DEFAULT_W,
  DOCK_DEFAULT_H,
} from './dockSize'

const TAB_BAR_H = 40
const EDGE = 8
const Z_BASE = 40
const Z_MOBILE = 60
const DOCK_POS_KEY = tlsKey('email.dock.single.pos.v1')
const DOCK_SIZE_KEY = tlsKey('email.dock.single.size.v1')

interface DockPos { right: number; bottom: number }

function clampDock(p: DockPos, w: number, h: number, vw: number, vh: number): DockPos {
  const maxRight = Math.max(EDGE, vw - w - EDGE)
  const maxBottom = Math.max(0, vh - h - EDGE)
  return {
    right: Math.min(Math.max(p.right, EDGE), maxRight),
    bottom: Math.min(Math.max(p.bottom, 0), maxBottom),
  }
}

function loadDockPos(): DockPos | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(DOCK_POS_KEY)
    if (!raw) return null
    const p = JSON.parse(raw) as Partial<DockPos>
    if (typeof p?.right === 'number' && typeof p?.bottom === 'number') {
      return { right: p.right, bottom: p.bottom }
    }
  } catch { /* noop */ }
  return null
}

function saveDockPos(p: DockPos): void {
  try { localStorage.setItem(DOCK_POS_KEY, JSON.stringify(p)) } catch { /* noop */ }
}

function useViewport(): { vw: number; vh: number } {
  const [v, setV] = useState(() =>
    typeof window === 'undefined'
      ? { vw: 1280, vh: 800 }
      : { vw: window.innerWidth, vh: window.innerHeight },
  )
  useEffect(() => {
    const h = () => setV({ vw: window.innerWidth, vh: window.innerHeight })
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])
  return v
}

/** Visible-viewport height for the mobile full-screen dock — keeps the
 *  editor + Send above the on-screen keyboard (fixed elements don't
 *  shrink for it on iOS/Android). */
function useVisualViewportHeight(enabled: boolean): number | null {
  const [h, setH] = useState<number | null>(() =>
    typeof window !== 'undefined' && window.visualViewport
      ? Math.round(window.visualViewport.height)
      : null,
  )
  useEffect(() => {
    if (!enabled) return
    const vv = window.visualViewport
    if (!vv) return
    const update = () => setH(Math.round(vv.height))
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [enabled])
  return h
}

export default function ComposeDock() {
  const windows = useComposeDockStore((s) => s.windows)
  const activeId = useComposeDockStore((s) => s.activeId)
  const setActive = useComposeDockStore((s) => s.setActive)
  const close = useComposeDockStore((s) => s.close)
  const closeAll = useComposeDockStore((s) => s.closeAll)
  const open = useComposeDockStore((s) => s.open)
  const navigate = useNavigate()

  const { vw, vh } = useViewport()
  const isMobile = vw < DOCK_MOBILE_BREAKPOINT
  const vvHeight = useVisualViewportHeight(isMobile)

  const [isMinimized, setMinimized] = useState(false)
  // FR (operator 2026-07-17): in-place expand. The pop-out navigates
  // to the full compose PAGE, which closes the dock and loses the
  // email the operator was reading — "reply opens in the drawer
  // without option to expand it". Maximize grows the dock to near
  // the whole viewport while staying on the current page.
  const [isMaximized, setMaximized] = useState(false)
  const [pos, setPos] = useState<DockPos>(() => loadDockPos() ?? { right: 24, bottom: 0 })
  // FR-0759: operator-resizable dock. Persisted size, clamped to the viewport
  // so reopening on a smaller screen can't strand it off-canvas.
  const [size, setSize] = useState<DockSize>(
    () => loadDockSize(DOCK_SIZE_KEY) ?? { width: DOCK_DEFAULT_W, height: DOCK_DEFAULT_H },
  )
  const [subjects, setSubjects] = useState<Record<string, string | null>>({})
  const attachCounts = useRef<Record<string, number>>({})
  const dragState = useRef<
    { pointerId: number; startX: number; startY: number; startRight: number; startBottom: number } | null
  >(null)
  const resizeState = useRef<
    { pointerId: number; startX: number; startY: number; startSize: DockSize } | null
  >(null)

  // Maximized: near-full viewport, capped so an ultrawide monitor
  // doesn't produce comically long text lines.
  const dockW = isMobile
    ? vw - 2 * EDGE
    : isMaximized
      ? Math.min(1280, vw - 2 * EDGE)
      : size.width
  const dockH = isMobile
    ? vh - 2 * EDGE
    : isMaximized
      ? vh - 2 * EDGE
      : size.height

  // Keep the dock size + position inside the viewport when it shrinks
  // (dragged/resized on a big monitor, reopened on a laptop).
  useEffect(() => {
    if (isMobile) return
    setSize((s) => clampDockSize(s, vw, vh))
  }, [vw, vh, isMobile])
  useEffect(() => {
    if (isMobile) return
    setPos((p) => clampDock(p, dockW, dockH, vw, vh))
  }, [vw, vh, dockW, dockH, isMobile])

  const onDragStart = (e: React.PointerEvent) => {
    if (isMobile || isMaximized || e.button !== 0) return
    e.currentTarget.setPointerCapture(e.pointerId)
    dragState.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startRight: pos.right,
      startBottom: pos.bottom,
    }
  }
  const onDragMove = (e: React.PointerEvent) => {
    const s = dragState.current
    if (!s || s.pointerId !== e.pointerId) return
    // Anchor is bottom-right: moving the mouse left/up grows the offsets.
    setPos(clampDock(
      { right: s.startRight - (e.clientX - s.startX), bottom: s.startBottom - (e.clientY - s.startY) },
      dockW, dockH, vw, vh,
    ))
  }
  const onDragEnd = (e: React.PointerEvent) => {
    if (dragState.current?.pointerId === e.pointerId) {
      try { (e.currentTarget as Element).releasePointerCapture?.(e.pointerId) } catch { /* noop */ }
      dragState.current = null
      saveDockPos(pos)
    }
  }

  // ── Resize from the top-left corner. The dock anchors bottom-right, so
  // dragging the handle up/left grows it; clamp to min/max + viewport.
  const onResizeStart = (e: React.PointerEvent) => {
    if (isMobile || isMaximized || e.button !== 0) return
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    resizeState.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startSize: size,
    }
  }
  const onResizeMove = (e: React.PointerEvent) => {
    const s = resizeState.current
    if (!s || s.pointerId !== e.pointerId) return
    e.stopPropagation()
    const next = resizeFromDelta(s.startSize, e.clientX - s.startX, e.clientY - s.startY)
    setSize(clampDockSize(next, vw, vh))
  }
  const onResizeEnd = (e: React.PointerEvent) => {
    if (resizeState.current?.pointerId === e.pointerId) {
      try { (e.currentTarget as Element).releasePointerCapture?.(e.pointerId) } catch { /* noop */ }
      resizeState.current = null
      saveDockSize(DOCK_SIZE_KEY, size)
    }
  }

  // ── Callbacks handed to the (memoised) compose bodies MUST be stable.
  // ComposePage reports its live subject + attachment count through
  // effects keyed on the callback identity (useComposePage.ts) — a fresh
  // identity each render would re-fire them and, with the subject write,
  // spin an infinite render loop. useCallback + a no-op-on-unchanged
  // setSubjects keeps both the identity and the state stable.
  const onSubjectChange = useCallback((id: string, s: string | null) => {
    setSubjects((prev) => (prev[id] === s ? prev : { ...prev, [id]: s }))
  }, [])
  const onAttachCountChange = useCallback((id: string, n: number) => {
    attachCounts.current[id] = n
  }, [])
  // Closing a dock window saves the FULL draft — text, recipients, subject AND
  // attachments — to Drafts and restores all of it on reopen (attachments via
  // IndexedDB + the cross-device server draft; flushed on unmount in
  // useComposePage). The old "attachments won't be restored" confirm
  // (BUG-2026-06-11-COMPOSE-ATTACH-LOSS) is therefore obsolete and was pure
  // friction, so closing is now a silent save like Gmail/Outlook.
  const closeWindow = useCallback((id: string) => {
    close(id)
  }, [close])
  const onSendFailed = useCallback((id: string) => {
    setMinimized(false)
    setActive(id)
  }, [setActive])

  if (windows.length === 0) return null

  const active = windows.find((w) => w.id === activeId) ?? windows[windows.length - 1]
  const tabs: DockTab[] = windows.map((w) => ({
    id: w.id,
    intent: w.intent,
    label: subjects[w.id] || w.initialSubject || titleForIntent(w.intent),
  }))

  const popOutActive = () => {
    const w = active
    close(w.id)
    const base = emailPath('/compose')
    if (w.intent === 'reply' && w.intentEmailId) navigate(`${base}?reply=${encodeURIComponent(w.intentEmailId)}`)
    else if (w.intent === 'reply_all' && w.intentEmailId) navigate(`${base}?reply_all=${encodeURIComponent(w.intentEmailId)}`)
    else if (w.intent === 'forward' && w.intentEmailId) navigate(`${base}?forward=${encodeURIComponent(w.intentEmailId)}`)
    // BUG-2026-06-23-COMPOSE-POPOUT-DUPLICATE-DRAFT — a `new` message must
    // carry its dock window's DRAFT id through as ?draft= so the full-page
    // composer resumes the SAME slot (`new:<draftId>` — the slot the dock
    // keyed via DockComposeBody's `draftIdOverride: w.draftId`). Navigating to
    // a bare /compose minted a FRESH draft id, so the pop-out opened a blank
    // message and left the original stranded as a separate draft. `close(w.id)`
    // already flushed this window's latest text to that slot above.
    else if (w.draftId) navigate(`${base}?draft=${encodeURIComponent(w.draftId)}`)
    else navigate(base)
  }

  const style: CSSProperties = isMobile
    ? {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        zIndex: Z_MOBILE, width: '100%',
        height: vvHeight != null ? `${vvHeight}px` : '100%',
      }
    : isMaximized
      ? {
          // Centered near-full-viewport overlay; sits above the reader
          // panes (same layer the mobile full-screen dock uses).
          position: 'fixed',
          right: Math.max(EDGE, Math.round((vw - dockW) / 2)),
          bottom: EDGE,
          zIndex: Z_MOBILE,
          width: dockW,
          height: isMinimized ? TAB_BAR_H : dockH,
        }
      : {
          position: 'fixed',
          right: pos.right,
          // Floor above the bottom bars (mobile nav + MobileDock); both
          // resolve to 0px on desktop. Same calc UniversalDock uses.
          bottom: `calc(var(--mobile-dock-h, 0px) + var(--email-hub-bottom-bar-h, 0px) + ${pos.bottom}px)`,
          zIndex: Z_BASE,
          width: dockW,
          height: isMinimized ? TAB_BAR_H : dockH,
          maxHeight: '92vh',
        }

  return createPortal(
    <div
      style={style}
      className="bg-card border border-border rounded-t-lg shadow-2xl flex flex-col overflow-hidden"
      role="dialog"
      aria-label={`${titleForIntent(active.intent)} compose window`}
      data-compose-dock
      onPointerMove={onDragMove}
      onPointerUp={onDragEnd}
      onPointerCancel={onDragEnd}
    >
      {/* Resize handle — top-left corner (the only free corner; the dock is
          pinned bottom-right). Desktop + expanded only. */}
      {!isMobile && !isMinimized && !isMaximized && (
        <div
          role="separator"
          aria-label="Resize compose window"
          aria-orientation="horizontal"
          className="absolute left-0 top-0 z-10 h-4 w-4 cursor-nwse-resize"
          style={{ touchAction: 'none' }}
          onPointerDown={onResizeStart}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeEnd}
          onPointerCancel={onResizeEnd}
        >
          <span className="pointer-events-none absolute left-1 top-1 h-2 w-2 border-l-2 border-t-2 border-muted-foreground/50" />
        </div>
      )}
      <DockTabStrip
        tabs={tabs}
        activeId={active.id}
        isMinimized={isMinimized}
        isMaximized={isMaximized}
        isMobile={isMobile}
        barHeight={TAB_BAR_H}
        onSelect={(id) => { setActive(id); setMinimized(false) }}
        onCloseTab={closeWindow}
        onNewTab={() => open(seedFromIntent('new', null))}
        onMinimizeToggle={() => setMinimized((m) => !m)}
        onMaximizeToggle={() => { setMaximized((m) => !m); setMinimized(false) }}
        onPopOut={popOutActive}
        onCloseAll={closeAll}
        onDragStart={onDragStart}
      />
      {/* Bodies stay mounted (hidden when not active) — see DockComposeBody. */}
      <div
        className="relative flex-1 min-h-0"
        style={isMinimized ? { display: 'none' } : undefined}
      >
        {windows.map((w) => (
          <DockComposeBody
            key={w.id}
            w={w}
            hidden={w.id !== active.id}
            onSubjectChange={onSubjectChange}
            onAttachCountChange={onAttachCountChange}
            onRequestClose={closeWindow}
            onSendFailed={onSendFailed}
          />
        ))}
      </div>
    </div>,
    document.body,
  )
}
